import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useBuilding } from "../../context/BuildingContext";
import { useAuth } from "@clerk/clerk-react";
import { budgetsApi } from "../../api/budgetsApi";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import EmptyState from "../../components/common/EmptyState";
import PageHeader from "../../components/common/PageHeader";
import { formatCurrency as formatPounds } from "../../utils/formatters";
import { Budget, BudgetType, BudgetStatus } from "@akfdev-com/shared";

// Budget amounts are stored as integer pence (see Constitution Principle 1).
const formatCurrency = (pence: number) => formatPounds((pence ?? 0) / 100);

// Format date range for display
const formatDateRange = (startDate: string, endDate: string): string => {
  const start = new Date(startDate).toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  const end = new Date(endDate).toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  return `${start} to ${end}`;
};

const BudgetsList: React.FC = () => {
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterByType, setFilterByType] = useState<BudgetType | "all">("all");

  const { currentBuilding } = useBuilding();
  const { getToken } = useAuth();

  useEffect(() => {
    const fetchBudgets = async () => {
      if (!currentBuilding?.id) return;

      try {
        setLoading(true);
        const token = await getToken();
        if (!token) {
          throw new Error("Authentication token not available");
        }
        const data = await budgetsApi.list(currentBuilding.id, token);
        setBudgets(data);
      } catch (err) {
        console.error("Error fetching budgets:", err);
        setError("Failed to load budgets. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchBudgets();
  }, [currentBuilding?.id, getToken]);

  // Filter budgets by selected type
  const filteredBudgets =
    filterByType === "all"
      ? budgets
      : budgets.filter((budget) => budget.budgetType === filterByType);

  // Calculate budget stats for the current view
  const calculateStats = () => {
    if (filteredBudgets.length === 0)
      return {
        totalBudgeted: 0,
        totalActual: 0,
        averageSpending: 0,
        spendingPercentage: 0,
      };

    const totalBudgeted = filteredBudgets.reduce(
      (sum, budget) => sum + budget.totalBudgeted,
      0,
    );
    const totalActual = filteredBudgets.reduce(
      (sum, budget) => sum + budget.totalActual,
      0,
    );
    const averageSpending = totalActual / filteredBudgets.length;
    const spendingPercentage =
      totalBudgeted > 0 ? (totalActual / totalBudgeted) * 100 : 0;

    return {
      totalBudgeted,
      totalActual,
      averageSpending,
      spendingPercentage: Math.round(spendingPercentage),
    };
  };

  const stats = calculateStats();

  const getStatusColor = (status: BudgetStatus) => {
    switch (status) {
      case "APPROVED":
        return "bg-green-100 text-green-800";
      case "DRAFT":
        return "bg-yellow-100 text-yellow-800";
      case "CLOSED":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getBudgetTypeLabel = (type: BudgetType) => {
    switch (type) {
      case "INCOME":
        return "Income Budget";
      case "EXPENSE":
        return "Expense Budget";
      case "SAVINGS":
        return "Savings Budget";
      default:
        return type;
    }
  };

  if (loading) {
    return <Spinner message="Loading budgets..." />;
  }

  if (error) {
    return <ErrorAlert message={error} />;
  }

  return (
    <div className="">
      <PageHeader
        title="Budgets"
        description={`Manage financial planning for ${
          currentBuilding?.name || "your building"
        }`}
        actions={
          <Link
            to="/dashboard/budgets/new"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Create Budget
          </Link>
        }
      />

      {/* Budget Type filter and stats */}
      <div className="mt-6 bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
        <div className="md:grid md:grid-cols-4 md:gap-6">
          <div className="md:col-span-1">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Filter by Type
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Select a budget type or view all budgets.
            </p>
          </div>
          <div className="mt-5 md:mt-0 md:col-span-3">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div className="sm:col-span-3">
                <label
                  htmlFor="typeFilter"
                  className="block text-sm font-medium text-gray-700"
                >
                  Budget Type
                </label>
                <select
                  id="typeFilter"
                  name="typeFilter"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                  value={filterByType}
                  onChange={(e) =>
                    setFilterByType(e.target.value as BudgetType | "all")
                  }
                >
                  <option value="all">All Budget Types</option>
                  <option value="INCOME">Income</option>
                  <option value="EXPENSE">Expense</option>
                  <option value="SAVINGS">Savings</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Budget Summary
          </h3>
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Total Budgeted
                </dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">
                  {formatCurrency(stats.totalBudgeted)}
                </dd>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Total Actual
                </dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">
                  {formatCurrency(stats.totalActual)}
                </dd>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Average Actual
                </dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">
                  {formatCurrency(stats.averageSpending)}
                </dd>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Actual vs Budget
                </dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">
                  {stats.spendingPercentage}%
                </dd>
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      stats.spendingPercentage > 100
                        ? "bg-red-600"
                        : "bg-primary-600"
                    }`}
                    style={{
                      width: `${Math.min(stats.spendingPercentage, 100)}%`,
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Budget List */}
      <div className="mt-8">
        {filteredBudgets.length === 0 ? (
          <EmptyState
            title="No budgets found"
            description={
              filterByType !== "all"
                ? `No ${filterByType.toLowerCase()} budgets found.`
                : "You don't have any budgets yet."
            }
            actionText={
              filterByType !== "all"
                ? "View all budgets"
                : "Create your first budget"
            }
            actionHref={
              filterByType !== "all" ? undefined : "/dashboard/budgets/new"
            }
            onAction={
              filterByType !== "all" ? () => setFilterByType("all") : undefined
            }
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
            }
          />
        ) : (
          <div className="space-y-8">
            {filteredBudgets.map((budget) => (
              <div
                key={budget.id}
                className="bg-white shadow overflow-hidden sm:rounded-lg"
              >
                <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                  <div>
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {budget.name}
                    </h3>
                    <p className="mt-1 max-w-2xl text-sm text-gray-500">
                      {formatDateRange(budget.startDate, budget.endDate)} •{" "}
                      {getBudgetTypeLabel(budget.budgetType)}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                        budget.status,
                      )}`}
                    >
                      {budget.status}
                    </span>
                    <Link
                      to={`/dashboard/budgets/${budget.id}`}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      View Details
                    </Link>
                  </div>
                </div>

                <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
                  {/* Budget Progress Bar */}
                  <div className="px-4 py-3 sm:px-6">
                    <div className="flex justify-between items-center">
                      <div className="text-sm text-gray-500">
                        Actual vs Budget
                      </div>
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(budget.totalActual)} of{" "}
                        {formatCurrency(budget.totalBudgeted)} (
                        {budget.totalBudgeted > 0
                          ? Math.round(
                              (budget.totalActual / budget.totalBudgeted) * 100,
                            )
                          : 0}
                        %)
                      </div>
                    </div>
                    <div className="mt-2 w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className={`h-2.5 rounded-full ${
                          budget.totalBudgeted > 0 &&
                          budget.totalActual / budget.totalBudgeted > 1
                            ? "bg-red-600"
                            : "bg-primary-600"
                        }`}
                        style={{
                          width: `${Math.min(
                            budget.totalBudgeted > 0
                              ? (budget.totalActual / budget.totalBudgeted) *
                                  100
                              : 0,
                            100,
                          )}%`,
                        }}
                      ></div>
                    </div>
                  </div>

                  {budget.notes && (
                    <div className="px-6 py-4">
                      <p className="text-sm font-medium text-gray-500">
                        Notes:
                      </p>
                      <p className="mt-1 text-sm text-gray-900">
                        {budget.notes}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BudgetsList;
